import React, { useState, useEffect } from 'react';
import { FaSearch, FaPlus, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import './css_pages/Users.css';
import {
  getAllUsers,
  createUser,
  updateUser,
  deleteUser
} from '../services/usersService';
import { getAllStores } from '../services/storesService';

// Helper para mostrar el nombre legible del rol
const roleLabel = (role) => {
  if (role === 'admin')   return 'Administrator';
  if (role === 'kitchen') return 'Kitchen';
  return 'Employee';
};

function Users() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStoresModal, setShowStoresModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userRole, setUserRole] = useState('employee');
  const [selectedStores, setSelectedStores] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [users, setUsers] = useState([]);
  const [allStores, setAllStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storeSearch, setStoreSearch] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchStores();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllUsers();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      setError('Error loading users');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await getAllStores();
      if (response.success) {
        setAllStores(response.data);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setUserName('');
    setUserEmail('');
    setUserPassword('');
    setUserRole('employee');
    setSelectedStores([]);
    setEditingId(null);
    setShowPassword(false);
  };

  const handleEditModal = (user) => {
    setShowModal(true);
    setUserName(user.name);
    setUserEmail(user.email);
    setUserPassword('');
    setUserRole(user.role);
    setSelectedStores(user.stores || []);
    setEditingId(user.id);
    setShowPassword(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setUserName('');
    setUserEmail('');
    setUserPassword('');
    setUserRole('employee');
    setSelectedStores([]);
    setEditingId(null);
    setShowPassword(false);
  };

  const handleOpenStoresModal = () => {
    setShowStoresModal(true);
    setStoreSearch('');
  };

  const handleToggleStore = (store) => {
    setSelectedStores(prev => {
      const isSelected = prev.some(s => s.id === store.id);
      if (isSelected) {
        return prev.filter(s => s.id !== store.id);
      } else {
        return [...prev, store];
      }
    });
  };

  const handleRemoveStore = (storeId) => {
    setSelectedStores(prev => prev.filter(s => s.id !== storeId));
  };

  const handleSaveUser = async () => {
    const normalizedName = userName.trim();
    const normalizedEmail = userEmail.trim();

    if (normalizedName === '') {
      alert('Please enter the name');
      return;
    }

    if (normalizedEmail === '') {
      alert('Please enter the email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    if (!editingId && userPassword.trim() === '') {
      alert('Please enter a password');
      return;
    }

    try {
      setLoading(true);

      const userData = {
        name: normalizedName,
        email: normalizedEmail,
        role: userRole,
        storeIds: selectedStores.map(s => s.id)
      };

      if (userPassword.trim() !== '') {
        userData.password = userPassword;
      }

      if (editingId) {
        const response = await updateUser(editingId, userData);
        if (response.success) {
          alert('User updated successfully');
          await fetchUsers();
          handleCloseModal();
        }
      } else {
        const response = await createUser(userData);
        if (response.success) {
          alert('User created successfully');
          await fetchUsers();
          handleCloseModal();
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        alert('Email already exists');
      } else if (error.response && error.response.status === 400) {
        alert(error.response.data.message || 'Validation error');
      } else {
        alert('Error saving user');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteUser(deleteId);
      if (response.success) {
        alert('User successfully removed');
        await fetchUsers();
      }
      handleCloseDeleteModal();
    } catch (error) {
      if (error.response && error.response.status === 403) {
        alert('Cannot delete the main administrator account');
      } else {
        alert('Error deleting user');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStoresForModal = allStores.filter(store =>
    store.name.toLowerCase().includes(storeSearch.toLowerCase())
  );

  return (
    <div className="users-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Users</h1>
        </div>
        <button className="btn-primary" onClick={handleOpenModal} disabled={loading}>
          <FaPlus /> Create new user
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchUsers}>Retry</button>
        </div>
      )}

      <div className="search-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="table-container desktop-only">
        {loading && <div className="loading">Loading...</div>}

        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Stores</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                  {loading ? 'Loading...' : 'No results found'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge badge-${user.role}`}>
                      {roleLabel(user.role)}
                    </span>
                  </td>
                  <td>
                    {user.stores && user.stores.length > 0 ? (
                      <div className="stores-list">
                        {user.stores.map((store, index) => (
                          <span key={store.id} className="store-tag">
                            {store.name}
                            {index < user.stores.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="no-stores">No stores assigned</span>
                    )}
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <button
                      className="btn-action"
                      onClick={() => handleEditModal(user)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => handleOpenDeleteModal(user.id)}
                      disabled={loading || user.id === 1}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className="users-cards-container mobile-only">
        {loading && <div className="loading">Loading...</div>}

        {filteredUsers.length === 0 ? (
          <div className="no-results">
            {loading ? 'Loading...' : 'No results found'}
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div key={user.id} className="user-card">
              <div className="user-card-header">
                <div className="user-header-info">
                  <h3 className="user-card-name">{user.name}</h3>
                  <span className={`badge badge-${user.role}`}>
                    {roleLabel(user.role)}
                  </span>
                </div>
              </div>

              <div className="user-card-body">
                <div className="user-card-row">
                  <div className="user-card-item full-width">
                    <span className="user-card-label">Email</span>
                    <span className="user-card-value">{user.email}</span>
                  </div>
                </div>

                <div className="user-card-row">
                  <div className="user-card-item full-width">
                    <span className="user-card-label">Stores Access</span>
                    <div className="user-card-value">
                      {user.stores && user.stores.length > 0 ? (
                        <div className="stores-tags-mobile">
                          {user.stores.map((store) => (
                            <span key={store.id} className="store-tag-mobile">
                              {store.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-stores">No stores assigned</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="user-card-row">
                  <div className="user-card-item">
                    <span className="user-card-label">Created At</span>
                    <span className="user-card-value">{formatDate(user.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="user-card-footer">
                <button
                  className="btn-card-action btn-edit"
                  onClick={() => handleEditModal(user)}
                  disabled={loading}
                >
                  Edit
                </button>
                <button
                  className="btn-card-action btn-delete-card"
                  onClick={() => handleOpenDeleteModal(user.id)}
                  disabled={loading || user.id === 1}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit user' : 'Create new user'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter user name"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="Enter email"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Password {editingId ? '(leave blank to keep current)' : '*'}</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                    placeholder={editingId ? 'Enter new password (optional)' : 'Enter password'}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="btn-toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  disabled={loading}
                >
                  <option value="employee">Employee</option>
                  <option value="kitchen">Kitchen</option>
                  <option value="admin">Administrator</option>
                </select>
                {userRole === 'kitchen' && (
                  <small className="form-hint">
                    Kitchen users only see the Pre-Batch page (view &amp; scale only — no sidebar, no create/edit).
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Stores Access</label>
                <div className="pick-container">
                  <button
                    className="btn-pick"
                    onClick={handleOpenStoresModal}
                    disabled={loading}
                    type="button"
                  >
                    <FaSearch /> Select Stores
                  </button>
                  {selectedStores.length > 0 && (
                    <div className="selected-stores">
                      {selectedStores.map((store) => (
                        <div key={store.id} className="selected-item">
                          {store.name}
                          <button
                            className="btn-remove-selection"
                            onClick={() => handleRemoveStore(store.id)}
                            type="button"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small className="form-hint">
                  Select which stores this user can access. Leave empty for all stores.
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveUser} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Seleccionar Stores */}
      {showStoresModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-picker" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Stores</h2>
              <button className="btn-close" onClick={() => setShowStoresModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Search..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="picker-list">
                <table className="picker-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Store Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStoresForModal.map((store) => {
                      const isSelected = selectedStores.some(s => s.id === store.id);
                      return (
                        <tr key={store.id} className={isSelected ? 'selected-row' : ''}>
                          <td>
                            <button
                              className={`btn-select-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleToggleStore(store)}
                            >
                              {isSelected ? '✓' : '○'}
                            </button>
                          </td>
                          <td>{store.name}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => setShowStoresModal(false)}
                >
                  Done ({selectedStores.length} selected)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm deletion</h2>
              <button className="btn-close" onClick={handleCloseDeleteModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to delete this user?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleConfirmDelete} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;